<script setup>
import { ref, reactive, watch, onMounted } from 'vue';
import ImageSelection from '../../common/ImageSelection.vue';

const props = defineProps({
    options: {
        type: Object,
        required: true
    }
});

const emit = defineEmits(['hide-modal']);

// --- State ---

const fileId = ref(0);
const align = ref(false);
const lockRatio = ref(true);
const imageEl = ref(props.options.image || null);
const parentEl = ref(null);

const alignmentClasses = [
    'text-editor-image-left',
    'text-editor-image-center',
    'text-editor-image-right'
];

const formData = reactive({
    altText: '',
    title: '',
    width: '',
    height: '',
    sizes: ''
});

// --- Methods ---

const setAlignment = () => {
    if (!imageEl.value) return;
    parentEl.value = imageEl.value.getParent();

    if (imageEl.value.hasClass(alignmentClasses[0])) align.value = 'left';
    else if (parentEl.value.hasClass(alignmentClasses[1])) align.value = 'center';
    else if (imageEl.value.hasClass(alignmentClasses[2])) align.value = 'right';
    else align.value = false;
};

const getOriginalDimensions = () => {
    if (!imageEl.value) return { width: 0, height: 0 };

    const currentW = imageEl.value.getSize('width');
    const currentH = imageEl.value.getSize('height');

    // Remove attributes temporarily to get natural size
    imageEl.value.removeAttributes(['width', 'height']);
    const dimensions = {
        width: imageEl.value.getSize('width'),
        height: imageEl.value.getSize('height')
    };

    // Restore
    imageEl.value.setAttributes({ width: currentW, height: currentH });
    return dimensions;
};

const setWidth = () => {
    if (lockRatio.value && formData.width) {
        const original = getOriginalDimensions();
        formData.height = Math.round(formData.width * (original.height / original.width));
    } else if (!formData.width) {
        formData.height = '';
    }
};

const setHeight = () => {
    if (lockRatio.value && formData.height) {
        const original = getOriginalDimensions();
        formData.width = Math.round(formData.height * (original.width / original.height));
    } else if (!formData.height) {
        formData.width = '';
    }
};

const refreshDimensions = () => {
    const original = getOriginalDimensions();
    formData.width = original.width;
    formData.height = original.height;
};

const onChange = (item) => {
    fileId.value = item.id;
    formData.width = item.width || formData.width;
    formData.height = item.height || formData.height;

    const src = `/asset/file/${fileId.value}`;

    if (imageEl.value) {
        imageEl.value.setAttribute('src', src);
        imageEl.value.data('cke-saved-src', src);
    } else {
        // Handle new insertion via CKEDITOR global
        // eslint-disable-next-line no-undef
        const style = new CKEDITOR.style({
            element: 'img',
            attributes: { src, id: 'temp_new_img' }
        });
        style.type = CKEDITOR.STYLE_INLINE;
        style.apply(props.options.editor);

        const element = props.options.editor.document.getById('temp_new_img');
        element.removeAttribute('id');
        props.options.editor.getSelection().selectElement(element);

        imageEl.value = element;
        parentEl.value = element.getParent();
    }
    props.options.editor.fire('change');
};

// --- Watchers ---

// Watch all text attributes
watch(() => [formData.altText, formData.title, formData.width, formData.height, formData.sizes], () => {
    if (!imageEl.value) return;

    imageEl.value.setAttributes({
        alt: formData.altText,
        title: formData.title,
        width: formData.width,
        height: formData.height,
        sizes: formData.sizes
    });
    props.options.editor.fire('change');
});

// Alignment watcher logic
watch(align, (newAlign) => {
    if (!imageEl.value) return;

    imageEl.value.removeClass(alignmentClasses[0]);
    parentEl.value?.removeClass(alignmentClasses[1]);
    imageEl.value.removeClass(alignmentClasses[2]);

    if (newAlign === 'left') imageEl.value.addClass(alignmentClasses[0]);
    if (newAlign === 'center') parentEl.value?.addClass(alignmentClasses[1]);
    if (newAlign === 'right') imageEl.value.addClass(alignmentClasses[2]);

    props.options.editor.fire('change');
});

// --- Initialization ---

onMounted(() => {
    if (imageEl.value) {
        fileId.value = parseInt(imageEl.value.$.currentSrc.split('/').pop());

        formData.altText = imageEl.value.getAttribute('alt') || '';
        formData.title = imageEl.value.getAttribute('title') || '';
        formData.width = imageEl.value.getAttribute('width') || '';
        formData.height = imageEl.value.getAttribute('height') || '';
        formData.sizes = imageEl.value.getAttribute('sizes') || '';

        setAlignment();
    }
});
</script>

<template>
    <div class="modal-dialog modal-lg">
        <div class="modal-content">
            <div class="modal-header">
                <h4 class="modal-title">&nbsp;</h4>
                <button aria-label="Close" class="btn-close" type="button" @click="$emit('hide-modal')"></button>
            </div>

            <div class="modal-body">
                <form class="form-horizontal" @submit.prevent>
                    <div class="mb-4 row">
                        <label class="col-sm-4 col-lg-3 form-label">Image</label>
                        <div class="col-sm-8 col-lg-9">
                            <ImageSelection
                                :file-id="fileId"
                                :on-change="onChange"
                                :form-id="props.options.formId"
                            />
                        </div>
                    </div>

                    <div class="mb-4 row">
                        <label class="col-sm-4 col-lg-3 form-label" for="altText">Alternative text</label>
                        <div class="col-sm-8 col-lg-9">
                            <input id="altText" v-model="formData.altText" :disabled="!fileId" class="form-control">
                        </div>
                    </div>

                    <div class="mb-4 row">
                        <label class="col-sm-4 col-lg-3 form-label" for="title">Title</label>
                        <div class="col-sm-8 col-lg-9">
                            <input id="title" v-model="formData.title" :disabled="!fileId" class="form-control">
                        </div>
                    </div>

                    <div class="mb-4 row">
                        <label class="col-sm-4 col-lg-3 form-label">Alignment</label>
                        <div class="col-sm-8 col-lg-9">
                            <div class="btn-group">
                                <button
                                    class="btn btn-light"
                                    :class="{ 'active': align === false }"
                                    :disabled="!fileId"
                                    type="button"
                                    @click="align = false"
                                >none</button>

                                <button
                                    class="btn btn-light"
                                    :class="{ 'active': align === 'left'}"
                                    :disabled="!fileId"
                                    type="button"
                                    @click="align = 'left'"
                                ><i class="bi-align-start"/></button>

                                <button
                                    class="btn btn-light"
                                    :class="{ 'active': align === 'center' }"
                                    :disabled="!fileId"
                                    type="button"
                                    @click="align = 'center'"
                                ><i class="bi-align-center"/></button>

                                <button
                                    class="btn btn-light"
                                    :class="{ 'active': align === 'right' }"
                                    :disabled="!fileId"
                                    type="button"
                                    @click="align = 'right'"
                                ><i class="bi-align-end"/></button>
                            </div>
                        </div>
                    </div>

                    <div class="mb-4 row">
                        <label class="col-sm-4 col-lg-3 form-label" for="width">Dimensions</label>
                        <div class="col-sm-8 col-lg-9 d-flex align-items-center">
                            <input
                                id="width" v-model="formData.width" :disabled="!fileId"
                                class="form-control me-1" placeholder="w" type="number" @input="setWidth"
                            >
                            x
                            <input
                                id="height" v-model="formData.height" :disabled="!fileId"
                                class="form-control ms-1" placeholder="h" type="number" @input="setHeight"
                            >

                            <button :disabled="!fileId" class="ms-1 btn btn-sm btn-light" type="button" @click="lockRatio = !lockRatio">
                                <span class="bi" :class="lockRatio ? 'bi-lock' : 'bi-unlock'"/>
                            </button>

                            <button :disabled="!fileId" class="ms-1 btn btn-sm btn-light" type="button" @click="refreshDimensions">
                                <i class="bi-arrow-clockwise"/>
                            </button>
                        </div>
                    </div>

                    <div class="mb-4 row">
                        <label class="col-sm-4 col-lg-3 form-label" for="sizes">Sizes</label>
                        <div class="col-sm-8 col-lg-9">
                            <input id="sizes" v-model="formData.sizes" :disabled="!fileId" class="form-control">
                        </div>
                    </div>
                </form>
            </div>
        </div>
    </div>
</template>